import { type LucideIcon } from "lucide-react";
import Image from "next/image";

import { darkGradient, lightGradient } from "./ctaButtonStyles";

export interface AboutUsCopy {
  header: {
    h2: string;
    h3: string;
    p1: string;
    p2: string;
    p3: string;
    p4: string;
    Icon: LucideIcon;
    imageAlt: string;
  };
  values: {
    Icon: LucideIcon;
    title: string;
    description: string;
  }[];
  cta: {
    text: string;
    href: string;
  };
}

export function AboutUs({ copy }: { copy: AboutUsCopy }) {
  const { header, values, cta } = copy;
  const HeaderIcon = header.Icon;

  return (
    <section id="o-nas" className="py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <div className="lg:w-1/2 relative">
            <div className="absolute inset-0 bg-blue-300/20 rounded-3xl transform -rotate-6 scale-105 z-0" />
            <div className="absolute inset-0 bg-lime-300/20 rounded-3xl transform -rotate-3 scale-105 z-0" />
            <div className="relative z-10 w-full h-[500px]">
              <Image
                src="/bluetooth-landing/about-us-team.jpg"
                alt={header.imageAlt}
                fill
                className="rounded-3xl shadow-xl object-cover"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl z-20 hidden md:block">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 p-3 rounded-full text-green-600">
                  <HeaderIcon size={24} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{header.p3}</p>
                  <p className="text-sm text-gray-500">{header.p4}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2">
          <h2 className="text-sm font-semibold bg-clip-text text-transparent uppercase tracking-widest mb-3" style={{ backgroundImage: darkGradient }}>
              {header.h2}
            </h2>
            <h3 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6">
              {header.h3}
            </h3>

            <p className="text-lg text-gray-600 mb-6">{header.p1}</p>

            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 mb-8">
              <p className="text-gray-700 italic">{header.p2}</p>
            </div>

            <ul className="space-y-4 mb-8">
              {values.map(value => (
                <li key={value.title} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1 relative">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center opacity-40" style={{ backgroundImage: lightGradient }}/>
                    <value.Icon className="absolute w-6 h-6 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-600" />
                  </div>
                  <span className="text-gray-700">{value.description}</span>
                </li>
              ))}
            </ul>
            <a
              href={cta.href}
              className="inline-block bg-background hover:bg-gray-800 text-white font-semibold py-3 px-8 rounded-full transition-colors"
            >
              {cta.text}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
